/**
 * El corte de la tira lateral, como función pura: la columna de Última hora se recorta a la
 * altura del cuerpo, y esta es la única decisión de ese recorte. Mismo criterio que
 * `ads/cadencia.ts` — la lógica fácil de romper vive fuera del componente, donde se testea
 * sin DOM. El componente solo mide y aplica.
 */

export interface FilaDeLaTira {
  /** Alto de la fila en píxeles, medido en el DOM (fraccionario: viene de `getBoundingClientRect`). */
  alto: number;
  /** Un robapáginas. Cambia qué pasa cuando no entra: ver `filasOcultas`. */
  esAviso: boolean;
}

/**
 * Índices de fila que **no se muestran**, dado el alto disponible en la columna.
 *
 * La regla es una sola — *nada se dibuja a medias* — con una asimetría deliberada entre las
 * dos clases de fila:
 *
 *  - Una **nota** que no entra entera corta la tira: ella y todo lo de abajo se ocultan. Ese
 *    "todo lo de abajo" no es celo: sin él, saltear una nota larga para colar la corta que
 *    sigue rompería el orden cronológico, que es lo único que la tira promete.
 *  - Un **aviso** que no entra entero se saltea y la tira sigue con las notas siguientes.
 *    Medio robapáginas es peor que no venderlo, pero perder por su culpa los 600 px de notas
 *    que sí entraban es peor todavía.
 *
 * Con `disponible <= 0` no oculta nada: es la medición de un contenedor que todavía no tiene
 * layout (o de una pantalla sin lateral), y ante la duda manda el `overflow: hidden` del CSS,
 * que a lo sumo corta feo pero nunca deja la columna en blanco.
 */
export function filasOcultas(filas: FilaDeLaTira[], disponible: number): Set<number> {
  const ocultas = new Set<number>();
  if (disponible <= 0) return ocultas;

  let usado = 0;
  for (const [i, fila] of filas.entries()) {
    if (usado + fila.alto <= disponible) {
      usado += fila.alto;
      continue;
    }
    ocultas.add(i);
    if (fila.esAviso) continue; // se saltea: no consume alto y la tira sigue
    for (let j = i + 1; j < filas.length; j += 1) ocultas.add(j);
    break;
  }
  return ocultas;
}
